import {
    UserGroupIcon,
    UserIcon,
    UsersIcon,
    BeakerIcon,
    CommandLineIcon,
    HeartIcon,
    DocumentDuplicateIcon,
    LinkIcon,
    NewspaperIcon,
    CameraIcon,
    ClockIcon,
    Squares2X2Icon,
    TagIcon,
    BuildingOffice2Icon
} from '@heroicons/react/24/outline'
import { WIREFRAME_BASE } from '../../pages/wireframes/routes'

// Admin items are clustered into these groups. The order here is the order the
// groups render under the "Admin" eyebrow; each item carries its group id so the
// data — not the render code — owns the grouping and ordering.
export type AdminNavGroupId = 'data-management' | 'system' | 'classification' | 'wireframes'

export const ADMIN_NAV_GROUPS: { id: AdminNavGroupId; label: string }[] = [
    { id: 'data-management', label: 'Data Management' },
    { id: 'system', label: 'System' },
    { id: 'classification', label: 'Classification' },
    { id: 'wireframes', label: 'Wireframes' }
]

/**
 * Which page the admin rail should highlight, as one object rather than a run of
 * positional booleans. With a dozen parameters of the same type the compiler
 * cannot tell a correct call site from a reordered one, so inserting an item
 * shifted every later flag onto the wrong nav entry and Scheduled jobs lit up
 * Classifier configuration. Naming the flags makes that mistake a type error.
 *
 * This is a subset of what whichPageAreWeOn returns, so callers can hand the
 * whole thing straight over.
 */
export type AdminNavPageFlags = {
    onSpamPage: boolean
    onTeamAdminPage: boolean
    onUserAdminPage: boolean
    onCommenterAdminPage: boolean
    onPostAdminPage: boolean
    onPromptTestingPage: boolean
    onServiceHealthPage: boolean
    onCookiesPage: boolean
    onReferrerAdminPage: boolean
    onWireframesPage: boolean
    onClassifierLibraryAdminPage: boolean
    onOrganisationsAdminPage: boolean
    onScreenshotAdminPage: boolean
    onScheduledJobsPage: boolean
}

export function getAdminNavigationItems(pages: AdminNavPageFlags) {
    const {
        onSpamPage,
        onTeamAdminPage,
        onUserAdminPage,
        onCommenterAdminPage,
        onPostAdminPage,
        onPromptTestingPage,
        onServiceHealthPage,
        onCookiesPage,
        onReferrerAdminPage,
        onWireframesPage,
        onClassifierLibraryAdminPage,
        onOrganisationsAdminPage,
        onScreenshotAdminPage,
        onScheduledJobsPage
    } = pages

    return [
        {
            name: 'Team',
            href: '#/admin/team',
            icon: UserGroupIcon,
            current: onTeamAdminPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            name: 'User',
            href: '#/admin/user',
            icon: UserIcon,
            current: onUserAdminPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            // UsersIcon rather than a second UserIcon so it scans apart from User.
            name: 'Commenter',
            href: '#/admin/commenter',
            icon: UsersIcon,
            current: onCommenterAdminPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            // Sits after Commenter: posts are the third leg of the data model
            // (profile -> post -> comment) and this is the only place they surface.
            name: 'Posts',
            href: '#/admin/post',
            icon: NewspaperIcon,
            current: onPostAdminPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            name: 'Cookies',
            href: '#/admin/cookie',
            icon: DocumentDuplicateIcon,
            current: onCookiesPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            name: 'Referrer',
            href: '#/admin/referrer',
            icon: LinkIcon,
            current: onReferrerAdminPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            // Ided like Scheduled jobs, because AdminSidebar hangs the
            // critical-services badge on it by id rather than by href.
            id: 'service-health',
            name: 'Service health',
            href: '#/admin/service-health',
            icon: HeartIcon,
            current: onServiceHealthPage,
            group: 'system' as AdminNavGroupId
        },
        {
            name: 'Classifier configuration',
            href: '#/admin/classifiers',
            icon: TagIcon,
            current: onClassifierLibraryAdminPage,
            group: 'classification' as AdminNavGroupId
        },
        {
            name: 'Manage spam model',
            href: '#/admin/spam-model',
            icon: BeakerIcon,
            current: onSpamPage,
            group: 'classification' as AdminNavGroupId
        },
        {
            // CommandLine rather than Sparkles — Ask Arwen owns the sparkles metaphor.
            name: 'Prompt tester',
            href: '#/admin/prompt-testing/text',
            icon: CommandLineIcon,
            current: onPromptTestingPage,
            group: 'classification' as AdminNavGroupId
        },
        {
            name: 'Screenshots',
            href: '#/admin/screenshot',
            icon: CameraIcon,
            current: onScreenshotAdminPage,
            group: 'classification' as AdminNavGroupId
        },
        {
            // Ided because AdminSidebar hangs the failing-jobs badge on it.
            // It used to find it by matching the href, which a route rename
            // would have broken silently — the badge would just stop
            // appearing, with nothing to notice.
            id: 'scheduled-jobs',
            name: 'Scheduled jobs',
            href: '#/admin/scheduled-jobs',
            icon: ClockIcon,
            current: onScheduledJobsPage,
            group: 'data-management' as AdminNavGroupId
        },
        {
            // The wireframes hub — an index of the proposed admin redesign screens.
            // No wireframe pill: this is the entry point to the prototypes, not itself
            // a proposed live screen.
            name: 'Wireframes',
            href: `#${WIREFRAME_BASE}`,
            icon: Squares2X2Icon,
            current: onWireframesPage,
            group: 'wireframes' as AdminNavGroupId
        },
        {
            // Organisations will eventually replace Teams, but not yet — surfaced in
            // the real admin IA as a wireframe proposal alongside the live Team item.
            // Renders the same component as the /wireframes hub.
            name: 'Organisations',
            href: '#/admin/organisations',
            icon: BuildingOffice2Icon,
            current: onOrganisationsAdminPage,
            wireframe: true,
            group: 'wireframes' as AdminNavGroupId
        }
    ]
}
